import React, { useEffect, useRef, useState } from "react";
import { MapPin, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { loadGoogleMaps } from "@/lib/maps";

export default function AddressStep({ value, onChange, onNext }) {
  const [text, setText]           = useState(value?.formatted || "");
  const [results, setResults]     = useState([]);
  const [open, setOpen]           = useState(false);
  const [loading, setLoading]     = useState(false);
  const [picked, setPicked]       = useState(!!value?.place_id);
  const [mapsReady, setMapsReady] = useState(false);

  const debounceRef  = useRef(null);
  const sessionRef   = useRef(null);
  const containerRef = useRef(null);

  /* ── Load Google Maps (new Places API v2) ── */
  useEffect(() => {
    loadGoogleMaps()
      .then(() => {
        // AutocompleteSuggestion lives on google.maps.places (new API)
        sessionRef.current = new window.google.maps.places.AutocompleteSessionToken();
        setMapsReady(true);
      })
      .catch((err) => {
        console.error("[AddressStep] Google Maps failed to load:", err);
      });
  }, []);

  /* ── Fetch suggestions using new AutocompleteSuggestion API ── */
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const q = text.trim();
    if (!mapsReady || q.length < 3 || picked) {
      setResults([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { AutocompleteSuggestion } = window.google.maps.places;
        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: q,
          sessionToken: sessionRef.current,
          includedRegionCodes: ["us"],
          locationBias: {
            west:  -74.2,
            east:  -73.5,
            south: 42.55,
            north: 43.0,
          },
          includedPrimaryTypes: ["street_address", "premise", "subpremise"],
        });

        setResults(
          (suggestions || []).map((s) => ({
            label:    s.placePrediction.text.toString(),
            place_id: s.placePrediction.placeId,
            prediction: s.placePrediction,
          }))
        );
        setOpen(true);
      } catch (err) {
        console.error("[AddressStep] Autocomplete error:", err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 280);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [text, mapsReady, picked]);

  /* ── Pick a suggestion — resolve lat/lng via Place.fetchFields ── */
  const pick = async (r) => {
    setText(r.label);
    setResults([]);
    setOpen(false);
    setPicked(true);

    try {
      const place = r.prediction.toPlace();
      await place.fetchFields({ fields: ["location", "formattedAddress"] });
      const loc = place.location;
      onChange({
        formatted: r.label,
        place_id:  r.place_id,
        lat:       loc ? loc.lat() : null,
        lng:       loc ? loc.lng() : null,
      });
    } catch (_) {
      // Still accept the pick even if geocode fails
      onChange({ formatted: r.label, place_id: r.place_id, lat: null, lng: null });
    }

    // Fresh session token after a completed pick (Google billing best practice)
    if (window.google?.maps?.places?.AutocompleteSessionToken) {
      sessionRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  };

  /* ── Reset pick state if user edits ── */
  const handleType = (e) => {
    setText(e.target.value);
    if (picked) {
      setPicked(false);
      onChange(null);
    }
    setOpen(true);
  };

  /* ── Close on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleNext = () => {
    if (!text || text.length < 6) {
      toast.error("Please enter your service address");
      return;
    }
    if (!picked || !value?.place_id) {
      toast.error("Please select an address from the suggestions list");
      return;
    }
    onNext();
  };

  return (
    <div data-testid="step-address">
      <h2
        style={{
          fontFamily: "'Geist', sans-serif",
          fontWeight: 600,
          fontSize: "clamp(1.9rem, 6vw, 2.8rem)",
          letterSpacing: "-0.04em",
          lineHeight: 0.95,
          color: "#f5f5f2",
          marginBottom: "12px",
        }}
      >
        Where should we{" "}
        <em
          style={{
            fontStyle: "italic",
            background:
              "linear-gradient(135deg, #8A8A8A 0%, #AAAAAA 50%, #8A8A8A 100%)",
            backgroundSize: "200% auto",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          park the van?
        </em>
      </h2>
      <p
        style={{
          color: "rgba(255,255,255,0.50)",
          fontSize: "14px",
          lineHeight: 1.65,
          marginBottom: "28px",
        }}
      >
        Niskayuna, NY only. Type your address then{" "}
        <strong style={{ color: "rgba(255,255,255,0.75)" }}>
          select it from the dropdown
        </strong>{" "}
        — we need the exact location.
      </p>

      <label
        style={{
          display: "block",
          fontSize: "10px",
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.40)",
          marginBottom: "10px",
        }}
      >
        Service Address
      </label>

      {/* Input + dropdown — single relative container */}
      <div ref={containerRef} style={{ position: "relative" }}>
        <MapPin
          size={15}
          style={{
            position: "absolute",
            left: "14px",
            top: "50%",
            transform: "translateY(-50%)",
              color: picked ? "#8A8A8A" : "rgba(255,255,255,0.35)",
            pointerEvents: "none",
            flexShrink: 0,
            transition: "color 0.25s",
            zIndex: 1,
          }}
        />
        <input
          className="input-luxe"
          style={{ paddingLeft: "42px", paddingRight: "44px" }}
          placeholder="Start typing your Niskayuna address…"
          value={text}
          onChange={handleType}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
            if (e.key === "Enter") { e.preventDefault(); handleNext(); }
          }}
          data-testid="address-input"
          autoComplete="off"
          spellCheck={false}
        />

        {/* Right indicator */}
        {loading ? (
          <Loader2
            size={14}
            style={{
              position: "absolute",
              right: "14px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "rgba(255,255,255,0.35)",
              animation: "spin 1s linear infinite",
            }}
          />
        ) : picked ? (
          <CheckCircle2
            size={16}
            style={{
              position: "absolute",
              right: "14px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#8A8A8A",
            }}
          />
        ) : null}

        {/* Dropdown */}
        <AnimatePresence>
          {open && results.length > 0 && (
            <motion.ul
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              data-testid="address-suggestions"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: "calc(100% + 6px)",
                zIndex: 9999,
                background: "rgba(8, 12, 20, 0.97)",
                backdropFilter: "blur(24px)",
                WebkitBackdropFilter: "blur(24px)",
                border: "none",
                borderRadius: "14px",
                overflow: "hidden",
                boxShadow: "0 20px 50px rgba(0,0,0,0.70)",
                padding: "4px",
                listStyle: "none",
                margin: 0,
              }}
            >
              {results.map((r, idx) => (
                <li
                  key={`${r.place_id}-${idx}`}
                  onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    padding: "12px 14px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "background 0.15s",
                    borderBottom:
                      idx < results.length - 1
                        ? "1px solid rgba(255,255,255,0.05)"
                        : "none",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(138,138,138,0.09)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <MapPin
                    size={13}
                    style={{
                      color: "rgba(170,170,170,0.70)",
                      marginTop: "2px",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "13px",
                      color: "rgba(255,255,255,0.88)",
                      lineHeight: 1.45,
                    }}
                  >
                    {r.label}
                  </span>
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>

      {/* Validation hints */}
      <AnimatePresence>
        {text.length > 3 && !picked && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{
              fontSize: "11px",
              color: "rgba(255,160,30,0.80)",
              marginTop: "8px",
              letterSpacing: "0.01em",
            }}
          >
            ↑ Select an address from the list above to continue
          </motion.p>
        )}
        {picked && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{
              fontSize: "11px",
              color: "rgba(170,170,170,0.80)",
              marginTop: "8px",
            }}
          >
            ✓ Address confirmed
          </motion.p>
        )}
      </AnimatePresence>

      <div style={{ marginTop: "36px", display: "flex", justifyContent: "flex-end" }}>
        <button
          className="btn-primary"
          onClick={handleNext}
          data-testid="address-next-button"
          style={{ opacity: picked ? 1 : 0.55 }}
        >
          Continue
          <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}
